import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { errorMessage } from '@/core/errorMessage';
import { Link, Navigate, useLocation } from 'react-router-dom';
import {
  Eye, EyeOff, Lock, Mail, BarChart3, ShieldCheck, Fingerprint, ScanFace,
  ArrowRight, MailCheck,
} from 'lucide-react';
import { supabase } from '@/core/supabase';
import { useSession } from '@/core/session';
import { isUsername, isValidUsername, isValidEmail } from '@/core/loginName';
import { invokeFunction } from '@/core/invokeFunction';
import {
  biometricStatus, unlockWithBiometric, type BiometricStatus,
} from '@/core/biometric';
import { Button } from '@/components/ui';
import { Spinner, FullScreenLoader } from '@/components/Loader';
import { useT } from '@/core/i18n';
import { LanguageToggle } from '@/components/LanguageToggle';
import { BrandLockup } from '@/components/brand';
import { LocationGlyph } from '@/components/icons';

/* =============================================================================
 * Signing in
 * -----------------------------------------------------------------------------
 * One composition, centred, at every width. There is no second column to fold
 * away, nothing that only exists above a breakpoint, and nothing that has to be
 * arranged differently on a handset — which is what makes it hold together from
 * a 360-pixel phone at a site gate to a widescreen in an office.
 *
 * -----------------------------------------------------------------------------
 * Why the brand is behind the card and not beside it
 * -----------------------------------------------------------------------------
 * A split screen with one half flooded in `--brand` has to answer an impossible
 * question eighteen times over: nine presets, two schemes, and a brand that is
 * a deep green in one and nearly white in another. Everything laid on it has to
 * flip with it, and the moment one thing does not, that half of the screen is
 * unreadable.
 *
 * So the brand is not a surface here. It is light: two large, soft washes of
 * `--brand` and `--accent` behind everything, at an opacity low enough that
 * they tint the page rather than colour it. The card on top is `--surface` and
 * the words on it are `--text`, which is the pairing every other screen in this
 * app is built from and the one the theme is guaranteed to get right.
 *
 * The result is a page that takes on the character of whichever preset is set —
 * warm under Desert, cool under Steel, deep under Plum — without any part of it
 * depending on a colour being light or dark.
 * ========================================================================== */

const FEATURES = [
  { icon: LocationGlyph, key: 'login.gpsAttendance' },
  { icon: BarChart3, key: 'login.jobCosting' },
  { icon: ShieldCheck, key: 'login.wpsPayroll' },
] as const;

export function LoginPage() {
  const { session } = useSession();
  const location = useLocation();
  const t = useT();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [biometric, setBiometric] = useState<BiometricStatus | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const [forgot, setForgot] = useState(false);

  // Asked once, on mount. A device with no reader, or somebody who has not
  // turned it on, never sees the button — rather than being shown one that
  // fails when pressed.
  useEffect(() => {
    let cancelled = false;
    void biometricStatus().then((status) => {
      if (!cancelled) setBiometric(status);
    });
    return () => { cancelled = true; };
  }, []);

  const canUnlock = Boolean(biometric?.available && biometric.enrolled);

  async function unlock() {
    setUnlocking(true);
    setError(null);

    const result = await unlockWithBiometric();

    if (!result.ok) {
      // Cancelling is a decision, not a fault. Saying "authentication failed"
      // to somebody who deliberately tapped Cancel reads as a broken app.
      if (!result.cancelled) setError(result.message);
      // A spent token clears itself, so re-read rather than assume.
      void biometricStatus().then(setBiometric);
      setUnlocking(false);
      return;
    }
    // The session listener takes it from here; the redirect below fires.
  }

  /* The redirect is checked before the loader, and the order is the point.
     With the loader above it, local `busy` state could outrank a real session:
     anything that stopped the form clearing that flag left somebody watching a
     spinner with a valid session sitting right behind it. The session is the
     fact — it decides, and the loader only covers the gap before it arrives. */
  if (session) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from && from !== '/login' ? from : '/'} replace />;
  }

  if ((busy || unlocking) && !error) {
    return <FullScreenLoader message={t('login.signingIn')} />;
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    /* Either identifies an account, and the two take different routes.
       -----------------------------------------------------------------------
       An address signs in here, directly, exactly as it always has. A name
       cannot: Supabase Auth knows accounts by one address each, so the name has
       to be resolved to one — and doing that in the browser would be an address
       book anybody could page through by guessing first names. resolve-login
       does the lookup and the password check in the same call and answers with
       a session or with nothing.

       Split this way on purpose. If that function is down or undeployed, every
       account with an email address still gets in. */
    const typed = email.trim().toLowerCase();

    if (isUsername(typed) ? !isValidUsername(typed) : !isValidEmail(typed)) {
      setError(t('login.badIdentifier'));
      setBusy(false);
      return;
    }

    try {
      if (isUsername(typed)) {
        const { session } = await invokeFunction<{
          session: { access_token: string; refresh_token: string };
        }>('resolve-login', { body: { username: typed, password } });

        const { error: setError_ } = await supabase.auth.setSession(session);
        if (setError_) throw new Error(setError_.message);
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: typed,
          password,
        });
        if (signInError) {
          throw new Error(
            signInError.message.toLowerCase().includes('invalid')
              ? t('login.wrongDetails')
              : signInError.message,
          );
        }
      }
    } catch (caught) {
      setError(errorMessage(caught, 'sign in'));
      setBusy(false);
    }
    // Otherwise the session is set and the redirect above fires on the next
    // render — deliberately nothing here, so there is no second source of truth.
  }

  return (
    <div className="relative min-h-dvh bg-bg text-text overflow-hidden">
      <Ambience />

      <div className="absolute top-4 end-4 z-20">
        <LanguageToggle />
      </div>

      {/* One scroll container for the whole page. `min-h-dvh` with the content
          centred means a tall window centres it and a short one — a phone with
          the keyboard up — simply scrolls, instead of clipping the button. */}
      <div className="relative z-10 min-h-dvh flex flex-col items-center justify-center
                      px-5 py-10 sm:px-6">
        <header className="w-full max-w-[25rem] flex flex-col items-center text-center">
          <BrandLockup size={44} stacked chip="auto" />

          <h1 className="text-[27px] sm:text-[31px] font-bold tracking-tight mt-8 text-balance">
            {t('login.welcome')}
          </h1>
          <p className="text-[14px] text-muted mt-2 leading-relaxed text-pretty">
            {t('login.subtitle')}
          </p>
        </header>

        {/* The card.
            -----------------------------------------------------------------
            Slightly translucent over the washes behind it, so the tint of the
            preset comes through the surface rather than stopping at its edge.
            The blur is what keeps it a surface rather than a stained pane. */}
        <div
          className="w-full max-w-[25rem] mt-8 p-6 sm:p-7
                     bg-surface/85 backdrop-blur-xl
                     border border-line rounded-card shadow-pop"
        >
          {canUnlock && (
            <>
              <Button
                variant="secondary"
                size="lg"
                className="w-full"
                onClick={() => void unlock()}
                icon={biometric?.label === 'face'
                  ? <ScanFace size={17} />
                  : <Fingerprint size={17} />}
              >
                {biometric?.label === 'face'
                  ? t('login.withFace')
                  : biometric?.label === 'fingerprint'
                    ? t('login.withFingerprint')
                    : t('login.withBiometrics')}
              </Button>

              <div className="flex items-center gap-3 my-6" aria-hidden="true">
                <span className="h-px flex-1 bg-line" />
                <span className="text-[10.5px] uppercase tracking-[0.16em] text-subtle">
                  {t('login.or')}
                </span>
                <span className="h-px flex-1 bg-line" />
              </div>
            </>
          )}

          {forgot ? <ForgotPassword onClose={() => setForgot(false)} /> : (
          <form onSubmit={onSubmit} className="flex flex-col gap-4">
            <Field label={t('login.identifier')} icon={<Mail size={16} />}>
              <input
                /* Not type="email". The browser refuses to submit `driver1`
                   against that type, which is the wall somebody hit trying to
                   make a login for a man with no mailbox. The rule lives in
                   loginName.ts, where this and the edge function that creates
                   the account both read the same one. */
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                /* What the keyboard reads to offer a saved login and the right
                   key layout. `autoCapitalize` matters more than it looks:
                   Android capitalises the first letter by default, and a login
                   starting with a capital fails to match the saved one, so
                   nothing is ever suggested. */
                autoComplete="username"
                name="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="next"
                placeholder={t('login.identifierExample')}
                className="w-full h-12 ps-11 pe-3 bg-transparent text-[16px] sm:text-[15px]
                           placeholder:text-subtle outline-none"
              />
            </Field>

            <Field label={t('login.password')} icon={<Lock size={16} />}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                name="password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
                placeholder="••••••••"
                className="w-full h-12 ps-11 pe-12 bg-transparent text-[16px] sm:text-[15px]
                           placeholder:text-subtle outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                className="absolute end-2 top-1/2 -translate-y-1/2 p-2 rounded-nested
                           text-subtle hover:text-text hover:bg-surface-2 transition-colors"
              >
                {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </Field>

            {/* Under the password, where somebody looks after mistyping it —
                not in a footer they would have to go hunting for. */}
            <div className="flex justify-end -mt-1">
              <button
                type="button"
                onClick={() => setForgot(true)}
                className="text-[12.5px] text-muted hover:text-brand transition-colors
                           underline underline-offset-2 decoration-line"
              >
                {t('login.forgot')}
              </button>
            </div>

            {error && (
              <p
                role="alert"
                className="text-[12.5px] text-danger bg-danger-subtle rounded-card
                           px-3.5 py-3 leading-snug"
              >
                {error}
              </p>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={busy}
              className="w-full mt-1"
              {...(busy ? { icon: <Spinner size={15} /> } : {})}
            >
              {busy ? t('login.signingIn') : t('login.signIn')}
              {/* After the words, so it reads as "sign in →" rather than an
                  icon button with a label. Button only has a leading `icon`
                  slot, and adding a trailing one to it for a single arrow
                  would be an API for one call site. Flipped in Arabic, where
                  forward is the other way. */}
              {!busy && <ArrowRight size={16} className="rtl:rotate-180" />}
            </Button>
          </form>
          )}
        </div>

        <p className="w-full max-w-[25rem] text-[13px] text-muted mt-6 text-center">
          {t('login.newCompany')}{' '}
          <Link
            to="/register"
            className="font-semibold text-brand hover:underline underline-offset-4"
          >
            {t('login.registerYours')}
          </Link>
        </p>

        {/* What this is, once, small, and under everything it explains. The old
            page gave this half the screen; it is worth a line. */}
        <ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-9">
          {FEATURES.map((feature) => (
            <li
              key={feature.key}
              className="inline-flex items-center gap-1.5 text-[11.5px] text-subtle"
            >
              <feature.icon size={13} className="shrink-0" />
              {t(feature.key)}
            </li>
          ))}
        </ul>

        <p className="max-w-[24rem] text-[11.5px] text-subtle/80 mt-5 text-center leading-relaxed">
          {t('login.askAdmin')}
        </p>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------------
 * The light behind everything
 * -----------------------------------------------------------------------------
 * Two soft washes and a fine grid, all drawn from tokens, all at an opacity low
 * enough to tint rather than colour. This is what carries the brand on this
 * page — and because nothing is placed *on* a brand-coloured surface, no part
 * of it has to know whether that colour is light or dark.
 *
 * Blur rather than a gradient function on purpose: `bg-brand` on a blurred
 * circle needs no colour arithmetic, so it works with any token the theme
 * defines without this file knowing what is in it.
 * -------------------------------------------------------------------------- */
/**
 * Fades the grid out towards the edges, so it never meets a corner squarely.
 *
 * `black` here is an alpha channel rather than a colour — a mask reads only
 * opacity — so it is the same value under every theme. Written once and spread,
 * because Safari still wants the prefixed property and two lines saying the
 * same thing is two lines to keep in step.
 */
const FADE = {
  maskImage: 'radial-gradient(ellipse at 50% 40%, black 30%, transparent 78%)',
  WebkitMaskImage: 'radial-gradient(ellipse at 50% 40%, black 30%, transparent 78%)',
} as const;

/* -----------------------------------------------------------------------------
 * Asking for a way back in
 * -----------------------------------------------------------------------------
 * One sentence back, whatever happens. Not a courtesy — an endpoint that says
 * "no such account" is a way to find out who works somewhere, and this one
 * answers anybody holding the anon key, which ships inside the app.
 *
 * So a name that does not exist, an account with no mailbox, and a mail server
 * having a bad day all produce the same reply. See request-password-reset,
 * where the same rule is enforced on the side that matters.
 * -------------------------------------------------------------------------- */
function ForgotPassword({ onClose }: { onClose: () => void }) {
  const t = useT();
  const [identifier, setIdentifier] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);

    /* Nothing is read from the answer and no failure is shown. Whatever comes
       back — a refusal, a timeout, a function that is not deployed — the
       person sees the same sentence, because any difference is the difference
       somebody is fishing for. */
    try {
      await invokeFunction('request-password-reset', {
        /* Where to come back to. The same build runs on a laptop and on the
           real URL, and a link that always points at one of them is useless
           from the other. The function checks this against its own allowlist
           before it uses it — see request-password-reset. */
        body: { identifier: identifier.trim(), origin: window.location.origin },
      });
    } catch {
      /* Deliberately silent. */
    }

    setSent(true);
    setBusy(false);
  }

  if (sent) {
    return (
      <div className="flex flex-col gap-4 text-center">
        <span className="mx-auto grid place-items-center w-12 h-12 rounded-full bg-ok-subtle text-ok">
          <MailCheck size={22} />
        </span>
        <div>
          <p className="text-[15px] font-semibold">{t('login.forgotSentTitle')}</p>
          <p className="text-[13px] text-muted mt-1.5 leading-relaxed">
            {t('login.forgotSentBody')}
          </p>
        </div>
        <Button variant="secondary" onClick={onClose} className="w-full">
          {t('login.backToSignIn')}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-4">
      <div>
        <p className="text-[15px] font-semibold">{t('login.forgotTitle')}</p>
        <p className="text-[13px] text-muted mt-1.5 leading-relaxed">
          {t('login.forgotBlurb')}
        </p>
      </div>

      <Field label={t('login.identifier')} icon={<Mail size={16} />}>
        <input
          type="text"
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          required
          autoFocus
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          placeholder={t('login.identifierExample')}
          className="w-full h-12 ps-11 pe-3 bg-transparent text-[16px] sm:text-[15px]
                     placeholder:text-subtle outline-none"
        />
      </Field>

      <Button
        type="submit"
        disabled={busy || !identifier.trim()}
        className="w-full"
        {...(busy ? { icon: <Spinner size={15} /> } : {})}
      >
        {busy ? t('login.forgotSending') : t('login.forgotSend')}
      </Button>

      <button
        type="button"
        onClick={onClose}
        className="text-[12.5px] text-muted hover:text-brand transition-colors"
      >
        {t('login.backToSignIn')}
      </button>
    </form>
  );
}

function Ambience() {
  return (
    <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
      <div
        className="absolute -top-40 -start-32 w-[38rem] h-[38rem] max-w-[120vw]
                   rounded-full bg-brand opacity-[0.13] blur-[130px]"
      />
      <div
        className="absolute -bottom-48 -end-40 w-[34rem] h-[34rem] max-w-[120vw]
                   rounded-full bg-accent opacity-[0.11] blur-[130px]"
      />

      {/* A grid in the text colour, which is near-black on a light theme and
          near-white on a dark one — so it reads as texture in both without
          being told which it is. */}
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage:
            'linear-gradient(currentColor 1px, transparent 1px),'
            + 'linear-gradient(90deg, currentColor 1px, transparent 1px)',
          backgroundSize: '46px 46px',
          ...FADE,
        }}
      />
    </div>
  );
}

/* -----------------------------------------------------------------------------
 * A labelled input
 * -----------------------------------------------------------------------------
 * The border lives on the wrapper rather than the input, so the whole field
 * lights up on focus including the icon and the reveal button — and so the two
 * fields cannot drift apart, which is what happens when the same six classes
 * are written twice.
 * -------------------------------------------------------------------------- */
function Field({
  label, icon, children,
}: { label: string; icon: ReactNode; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-[12px] font-semibold text-muted">{label}</span>
      <div
        className="relative bg-surface-2 border border-line rounded-card
                   transition-colors focus-within:border-brand
                   focus-within:ring-2 focus-within:ring-brand-subtle"
      >
        <span className="absolute start-3.5 top-1/2 -translate-y-1/2 text-subtle pointer-events-none">
          {icon}
        </span>
        {children}
      </div>
    </label>
  );
}
